import {gql} from '@apollo/client';
import {compact, groupByUniqueKey, objectFromEntries} from '@benchling/js-utils';

import {ResidueType} from 'biojs/BioConstants';
import {
  SequenceType,
  SequenceVersionSpecifierInput,
  AaSequenceVersionSpecifierInput,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  AssemblyAaSequenceHistoricalBasicContentsQuery,
  AssemblyAaSequenceHistoricalBasicContentsQueryVariables,
} from 'molbio/bulk-assembly/__generated/AssemblyAaSequenceHistoricalBasicContentsQuery';
import {
  AssemblyFragmentPolymersCurrentBasicContentsQuery,
  AssemblyFragmentPolymersCurrentBasicContentsQueryVariables,
  AssemblyFragmentPolymersCurrentBasicContentsQuery_polymers_AaSequenceNode as AaSequenceCurrentBasicContentsGQL,
  AssemblyFragmentPolymersCurrentBasicContentsQuery_polymers_SequenceNode as SequenceCurrentBasicContentsGQL,
} from 'molbio/bulk-assembly/__generated/AssemblyFragmentPolymersCurrentBasicContentsQuery';
import {
  AssemblySequenceHistoricalBasicContentsQuery,
  AssemblySequenceHistoricalBasicContentsQueryVariables,
} from 'molbio/bulk-assembly/__generated/AssemblySequenceHistoricalBasicContentsQuery';
import {BasicPolymerInfo, BulkAssemblyProcedure} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {isBAId} from 'molbio/bulk-assembly/BulkAssemblyValues';
import {polymerInfoFromGQL} from 'molbio/bulk-assembly/util/BulkAssemblyUtil';

export const ASSEMBLY_FRAGMENT_SEQUENCE_CURRENT_BASIC_CONTENTS_FRAGMENT = gql`
  fragment AssemblyFragmentSequenceCurrentBasicContentsFragment on SequenceNode {
    id
    name
    sequenceType
    experimentalCurrentVersion {
      dbId
      bases
      circular
      startIndex
    }
  }
`;

export const ASSEMBLY_FRAGMENT_AA_SEQUENCE_CURRENT_BASIC_CONTENTS_FRAGMENT = gql`
  fragment AssemblyFragmentAaSequenceCurrentBasicContentsFragment on AaSequenceNode {
    id
    name
    experimentalCurrentVersion {
      dbId
      aminoAcids
    }
  }
`;

/**
 * Query for basic current data of a single polymer (Sequence or Protein).
 */
export const ASSEMBLY_FRAGMENT_POLYMER_CURRENT_BASIC_CONTENTS_QUERY = gql`
  query AssemblyFragmentPolymerCurrentBasicContentsQuery($id: StrictString!)
  @skipAudit
  @engComponent(name: MOLBIO) {
    polymer(id: $id) {
      ...AssemblyFragmentSequenceCurrentBasicContentsFragment
      ...AssemblyFragmentAaSequenceCurrentBasicContentsFragment
      ...InaccessibleInfoGraphQL
    }
  }
  ${ASSEMBLY_FRAGMENT_SEQUENCE_CURRENT_BASIC_CONTENTS_FRAGMENT}
  ${ASSEMBLY_FRAGMENT_AA_SEQUENCE_CURRENT_BASIC_CONTENTS_FRAGMENT}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Query for basic current data of a list of polymers (Sequences or Proteins).
 */
export const ASSEMBLY_FRAGMENT_POLYMERS_CURRENT_BASIC_CONTENTS_QUERY = gql`
  query AssemblyFragmentPolymersCurrentBasicContentsQuery($ids: [StrictString!]!)
  @skipAudit
  @engComponent(name: MOLBIO) {
    polymers(ids: $ids) {
      ...AssemblyFragmentSequenceCurrentBasicContentsFragment
      ...AssemblyFragmentAaSequenceCurrentBasicContentsFragment
      ...InaccessibleInfoGraphQL
    }
  }
  ${ASSEMBLY_FRAGMENT_SEQUENCE_CURRENT_BASIC_CONTENTS_FRAGMENT}
  ${ASSEMBLY_FRAGMENT_AA_SEQUENCE_CURRENT_BASIC_CONTENTS_FRAGMENT}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const ASSEMBLY_SEQUENCE_HISTORICAL_BASIC_CONTENTS_QUERY = gql`
  query AssemblySequenceHistoricalBasicContentsQuery(
    $sequenceVersionSpecifiers: [SequenceVersionSpecifierInput!]!
  ) @skipAudit @engComponent(name: MOLBIO) {
    sequenceVersions(sequenceVersionSpecifiers: $sequenceVersionSpecifiers) {
      ... on SequenceVersionNode {
        dbId
        sequence {
          id
          sequenceType
        }
        bases
        circular
        startIndex
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const ASSEMBLY_AA_SEQUENCE_HISTORICAL_BASIC_CONTENTS_QUERY = gql`
  query AssemblyAaSequenceHistoricalBasicContentsQuery(
    $aaSequenceVersionSpecifiers: [AaSequenceVersionSpecifierInput!]!
  ) @skipAudit @engComponent(name: MOLBIO) {
    aaSequenceVersions(aaSequenceVersionSpecifiers: $aaSequenceVersionSpecifiers) {
      ... on AaSequenceVersionNode {
        dbId
        aaSequence {
          id
        }
        aminoAcids
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface NameAndLatestVersion {
  name: string;
  experimentalLatestVersionId: number;
}

export function versionKey(polymerId: string, polymerVersionDbId: number): string {
  return `${polymerId}:${polymerVersionDbId}`;
}

/**
 * Immutable class holding server-provided polymer data for use in rendering the bulk assembly UI.
 */
export default class BulkAssemblyPolymerData {
  constructor(
    private readonly polymerNameAndLatestVersionById: {[id: string]: NameAndLatestVersion},
    private readonly basicPolymerInfoByKey: {[versionKey: string]: BasicPolymerInfo | undefined}
  ) {}

  /**
   * Get the name of any polymer referenced by a fragment.
   */
  getName(polymerId: string): string | null {
    return this.polymerNameAndLatestVersionById[polymerId]?.name ?? null;
  }

  /**
   * Get the latest version of a polymer. This is available for polymers where the latest version
   * is not present in the procedure JSON, and is used to autofill new rows with a version.
   */
  getLatestVersionDbId(polymerId: string): number | null {
    return this.polymerNameAndLatestVersionById[polymerId]?.experimentalLatestVersionId ?? null;
  }

  /**
   * Get the basic contents of a polymer version. This is loaded for all polymer versions saved in
   * procedure JSON and all polymer versions returned from the latest version query.
   */
  getBasicPolymerInfo(polymerId: string, polymerVersionDbId: number): BasicPolymerInfo | null {
    return this.basicPolymerInfoByKey[versionKey(polymerId, polymerVersionDbId)] ?? null;
  }
}

function getAllFragmentSequenceIds(state: BulkAssemblyProcedure): Array<string> {
  return compact(
    state.fragments.map((fragment) => (isBAId(fragment.sequence) ? fragment.sequence.id : null))
  );
}

function getAllFragmentAaSequenceIds(state: BulkAssemblyProcedure): Array<string> {
  return compact(state.fragments.map((fragment) => (isBAId(fragment.protein) ? fragment.protein.id : null)));
}

function getAllFragmentPreferredPrimerIds(state: BulkAssemblyProcedure): Array<string> {
  return compact(
    state.fragments.flatMap((fragment) => [
      isBAId(fragment.preferredPrimer5) ? fragment.preferredPrimer5.id : null,
      isBAId(fragment.preferredPrimer3) ? fragment.preferredPrimer3.id : null,
    ])
  );
}

/**
 * Load basic contents information for specified sequence versions, enough information to display
 * endpoints, compute length, perform basic validation, and calculate cut sites if applicable.
 *
 * This information is loaded for all versions specified directly in the fragments information as
 * well as any latest versions loaded due to the version being missing.
 */
function useBasicPolymerInfosByKey(
  state: BulkAssemblyProcedure | null,
  polymersCurrentBasicContentsData: Array<AaSequenceCurrentBasicContentsGQL | SequenceCurrentBasicContentsGQL>
): {
  [versionKey: string]: BasicPolymerInfo | undefined;
} {
  const currentPolymerInfoById = groupByUniqueKey(polymersCurrentBasicContentsData, ({id}) => id);
  const basicPolymerInfos: {[versionKey: string]: BasicPolymerInfo} = objectFromEntries(
    polymersCurrentBasicContentsData.map((basicContentsData) => [
      versionKey(basicContentsData.id, basicContentsData.experimentalCurrentVersion.dbId),
      polymerInfoFromGQL(basicContentsData),
    ])
  );
  let historicalSequenceVersionSpecifiers: Array<SequenceVersionSpecifierInput>;
  let historicalAaSequenceVersionSpecifiers: Array<AaSequenceVersionSpecifierInput>;
  if (state != null) {
    historicalSequenceVersionSpecifiers = state.fragments.flatMap((fragment) =>
      isBAId(fragment.sequence) &&
      fragment.sequenceVersionDbId != null &&
      currentPolymerInfoById[fragment.sequence.id] != null &&
      currentPolymerInfoById[fragment.sequence.id].experimentalCurrentVersion.dbId !==
        fragment.sequenceVersionDbId
        ? [{sequenceId: fragment.sequence.id, versionDbId: fragment.sequenceVersionDbId}]
        : []
    );
    historicalAaSequenceVersionSpecifiers = state?.fragments.flatMap((fragment) =>
      isBAId(fragment.protein) &&
      fragment.proteinVersionDbId != null &&
      currentPolymerInfoById[fragment.protein.id] != null &&
      currentPolymerInfoById[fragment.protein.id].experimentalCurrentVersion.dbId !==
        fragment.proteinVersionDbId
        ? [{aaSequenceId: fragment.protein.id, versionDbId: fragment.proteinVersionDbId}]
        : []
    );
  } else {
    historicalSequenceVersionSpecifiers = [];
    historicalAaSequenceVersionSpecifiers = [];
  }

  const historicalSequencesContentResult = useSkippableBenchlingQuery<
    AssemblySequenceHistoricalBasicContentsQuery,
    AssemblySequenceHistoricalBasicContentsQueryVariables
  >(ASSEMBLY_SEQUENCE_HISTORICAL_BASIC_CONTENTS_QUERY, {
    variables: {
      sequenceVersionSpecifiers: historicalSequenceVersionSpecifiers,
    },
    skip: historicalSequenceVersionSpecifiers.length === 0,
  });
  const historicalAaSequencesContentResult = useSkippableBenchlingQuery<
    AssemblyAaSequenceHistoricalBasicContentsQuery,
    AssemblyAaSequenceHistoricalBasicContentsQueryVariables
  >(ASSEMBLY_AA_SEQUENCE_HISTORICAL_BASIC_CONTENTS_QUERY, {
    variables: {
      aaSequenceVersionSpecifiers: historicalAaSequenceVersionSpecifiers,
    },
    skip: historicalAaSequenceVersionSpecifiers.length === 0,
  });

  if (historicalSequencesContentResult.type === QueryResultType.SUCCESS) {
    for (const sequenceVersion of historicalSequencesContentResult.data.sequenceVersions) {
      if (!isInaccessible(sequenceVersion)) {
        basicPolymerInfos[versionKey(sequenceVersion.sequence.id, sequenceVersion.dbId)] = {
          residues: sequenceVersion.bases,
          circular: sequenceVersion.circular,
          startIndex: sequenceVersion.startIndex,
          singleStranded: sequenceVersion.sequence.sequenceType !== SequenceType.ds,
          residueType: ResidueType.NUCLEOTIDE,
        };
      }
    }
  }
  if (historicalAaSequencesContentResult.type === QueryResultType.SUCCESS) {
    for (const proteinVersion of historicalAaSequencesContentResult.data.aaSequenceVersions) {
      if (!isInaccessible(proteinVersion)) {
        basicPolymerInfos[versionKey(proteinVersion.aaSequence.id, proteinVersion.dbId)] = {
          residues: proteinVersion.aminoAcids,
          circular: false,
          startIndex: 1,
          singleStranded: true,
          residueType: ResidueType.AA,
        };
      }
    }
  }
  return basicPolymerInfos;
}

/**
 * Query for all polymer-related information (name, residues, etc) that will be needed to render the
 * bulk assembly UI.
 */
export function useBulkAssemblyPolymerData(state: BulkAssemblyProcedure | null): BulkAssemblyPolymerData {
  const allAssemblyPolymerIds: Set<string> = state
    ? new Set([
        ...getAllFragmentSequenceIds(state),
        ...getAllFragmentPreferredPrimerIds(state),
        ...getAllFragmentAaSequenceIds(state),
      ])
    : new Set();
  const polymersCurrentBasicContentsResult = useSkippableBenchlingQuery<
    AssemblyFragmentPolymersCurrentBasicContentsQuery,
    AssemblyFragmentPolymersCurrentBasicContentsQueryVariables
  >(ASSEMBLY_FRAGMENT_POLYMERS_CURRENT_BASIC_CONTENTS_QUERY, {
    variables: {ids: Array.from(allAssemblyPolymerIds)},
    skip: allAssemblyPolymerIds.size === 0,
  });
  const polymersCurrentBasicContentsData =
    polymersCurrentBasicContentsResult.type === QueryResultType.SUCCESS
      ? accessibleObjs(polymersCurrentBasicContentsResult.data.polymers)
      : [];
  // Extract name and latest version info for every accessible polymer. Primer oligos are included, although only their names are needed.
  const polymerNameAndLatestVersionById = objectFromEntries(
    polymersCurrentBasicContentsData.map(({id, name, experimentalCurrentVersion}) => [
      id,
      {name, experimentalLatestVersionId: experimentalCurrentVersion.dbId},
    ])
  );
  const basicPolymerInfoByKey = useBasicPolymerInfosByKey(state, polymersCurrentBasicContentsData);
  return new BulkAssemblyPolymerData(polymerNameAndLatestVersionById, basicPolymerInfoByKey);
}
